import { TCompanyUserForm } from "@/features/company/types/CompanyUserForm";
import { TCompanyUserPermissions } from "@/types/enum";

type PermissionFieldKey = keyof NonNullable<TCompanyUserForm["permissions"]>;

export type TPermissionFieldConfig = {
  field: PermissionFieldKey;
  defaultValue: boolean;
  active?: boolean;
  indented?: boolean;
};

// `field` doubles as the permission code shared with BE (same camelCase shape, e.g. `examStepEdit`),
// and as the lookup key into the `EUserPermission` enum endpoint that provides the display label.
// Only entries with `active: true` are editable and submitted on save; the rest are display-only
// checkboxes seeded from `defaultValue`.
export const PERMISSION_FIELDS: TPermissionFieldConfig[] = [
  { field: "dashboardView", defaultValue: true },
  { field: "examStepView", defaultValue: true },
  { field: "examStepEdit", defaultValue: false, active: true, indented: true },
  { field: "learningProgressView", defaultValue: true },
  { field: "learnerInfoView", defaultValue: true },
  { field: "progressReportView", defaultValue: true },
];

export const getDefaultPermissions = (): TCompanyUserPermissions =>
  Object.fromEntries(PERMISSION_FIELDS.map((f) => [f.field, f.defaultValue])) as TCompanyUserPermissions;

export const mapPermissionsToForm = (
  permissions?: TCompanyUserPermissions | null,
): TCompanyUserPermissions =>
  Object.fromEntries(
    PERMISSION_FIELDS.map((f) => [f.field, permissions?.[f.field] ?? f.defaultValue]),
  ) as TCompanyUserPermissions;

export const pickSubmittablePermissions = (
  permissions?: TCompanyUserPermissions,
): Partial<TCompanyUserPermissions> =>
  Object.fromEntries(
    PERMISSION_FIELDS.filter((f) => f.active).map((f) => [f.field, !!permissions?.[f.field]]),
  ) as Partial<TCompanyUserPermissions>;
